import type {
  FirmClient,
  FirmClientIndividual,
  MonetaryAmount,
  MonetaryAmountInput,
  QuarterlyProgressReportReview,
} from '../generated/graphql';
import {
  FirmClientMode,
  FirmClientStatus,
  QuarterlyProgressReportReviewSection,
  QuarterlyProgressReportReviewStatus,
} from '../generated/graphql';
import { isValid, parseISO } from 'date-fns';

export const quarterlyProgressReportReviewSections = [
  QuarterlyProgressReportReviewSection.Assets,
  QuarterlyProgressReportReviewSection.Debts,
  QuarterlyProgressReportReviewSection.Spending,
] as const;

type QuarterlyProgressReportReviewSectionReviewedAtKey = 'assetsReviewedAt' | 'debtsReviewedAt' | 'spendingReviewedAt';

type QuarterlyProgressReportReviewSectionCompletion = Partial<
  Record<QuarterlyProgressReportReviewSectionReviewedAtKey, string | null | undefined>
>;
type QuarterlyProgressReportReviewPeriod = Partial<Record<'periodStart' | 'periodEnd', string | null | undefined>>;
type QuarterlyProgressReportAdvisorReview = Pick<
  QuarterlyProgressReportReview,
  'clientReviewCompletedAt' | 'clientReviewStartedAt' | 'lastRequestedAt' | 'status'
>;

export type QuarterlyProgressReportAdvisorAction = 'sendRequest' | 'sendReminder' | 'startNewReview';
export type QuarterlyProgressReportAdvisorLifecycle = 'notRequested' | 'sent' | 'opened' | 'finalizing' | 'completed';

export type QuarterlyProgressReportAdvisorPresentationState = {
  action: QuarterlyProgressReportAdvisorAction | null;
  actionAvailable: boolean;
  lifecycle: QuarterlyProgressReportAdvisorLifecycle;
  reminderAvailableAt: string | null;
};

// Mirrors backend quarterlyProgressReportReminderCooldown; the server remains authoritative.
const quarterlyProgressReportReminderCooldownMilliseconds = 24 * 60 * 60 * 1000;

const quarterlyProgressReportDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const reviewedAtKeyBySection: Record<
  QuarterlyProgressReportReviewSection,
  QuarterlyProgressReportReviewSectionReviewedAtKey
> = {
  [QuarterlyProgressReportReviewSection.Assets]: 'assetsReviewedAt',
  [QuarterlyProgressReportReviewSection.Debts]: 'debtsReviewedAt',
  [QuarterlyProgressReportReviewSection.Spending]: 'spendingReviewedAt',
};

export function isQuarterlyProgressReportReviewSection(
  value: FormDataEntryValue | null
): value is QuarterlyProgressReportReviewSection {
  return (
    typeof value === 'string' &&
    quarterlyProgressReportReviewSections.includes(value as QuarterlyProgressReportReviewSection)
  );
}

export function getQuarterlyProgressReportReviewSectionReviewedAt(
  review: QuarterlyProgressReportReviewSectionCompletion,
  section: QuarterlyProgressReportReviewSection
) {
  return review[reviewedAtKeyBySection[section]];
}

export function getQuarterlyProgressReportReviewProgress(review: QuarterlyProgressReportReviewSectionCompletion) {
  const completed = quarterlyProgressReportReviewSections.filter((section) =>
    Boolean(getQuarterlyProgressReportReviewSectionReviewedAt(review, section))
  ).length;

  return {
    completed,
    total: quarterlyProgressReportReviewSections.length,
  };
}

export function hasQuarterlyProgressReportMetricValue(value: unknown) {
  return value !== null && value !== undefined;
}

export function canRequestQuarterlyProgressReportReview(
  firmClient:
    | (Pick<FirmClient, 'mode' | 'status'> & {
        individuals: ReadonlyArray<Pick<FirmClientIndividual, 'emailAddress' | 'userID'>>;
      })
    | null
    | undefined
) {
  return Boolean(
    firmClient?.mode === FirmClientMode.OnboardingCompleted &&
      firmClient.status === FirmClientStatus.Active &&
      firmClient.individuals.some((individual) => Boolean(individual.userID && individual.emailAddress))
  );
}

export function getQuarterlyProgressReportAdvisorPresentationState(
  review: QuarterlyProgressReportAdvisorReview | null | undefined,
  now = new Date()
): QuarterlyProgressReportAdvisorPresentationState {
  if (!review) {
    return {
      action: 'sendRequest',
      actionAvailable: true,
      lifecycle: 'notRequested',
      reminderAvailableAt: null,
    };
  }

  if (review.status === QuarterlyProgressReportReviewStatus.Complete) {
    return {
      action: 'startNewReview',
      actionAvailable: true,
      lifecycle: 'completed',
      reminderAvailableAt: null,
    };
  }

  if (review.clientReviewCompletedAt) {
    return {
      action: null,
      actionAvailable: false,
      lifecycle: 'finalizing',
      reminderAvailableAt: null,
    };
  }

  const lifecycle =
    review.status === QuarterlyProgressReportReviewStatus.InProgress || review.clientReviewStartedAt
      ? 'opened'
      : review.lastRequestedAt
      ? 'sent'
      : 'notRequested';

  if (lifecycle === 'notRequested') {
    return {
      action: 'sendRequest',
      actionAvailable: true,
      lifecycle,
      reminderAvailableAt: null,
    };
  }

  if (!review.lastRequestedAt) {
    return {
      action: 'sendReminder',
      actionAvailable: true,
      lifecycle,
      reminderAvailableAt: null,
    };
  }

  const lastRequestedAt = parseISO(review.lastRequestedAt);
  if (!isValid(lastRequestedAt) || !isValid(now)) {
    return {
      action: 'sendReminder',
      actionAvailable: false,
      lifecycle,
      reminderAvailableAt: null,
    };
  }

  const reminderAvailableAt = new Date(lastRequestedAt.getTime() + quarterlyProgressReportReminderCooldownMilliseconds);

  return {
    action: 'sendReminder',
    actionAvailable: now.getTime() >= reminderAvailableAt.getTime(),
    lifecycle,
    reminderAvailableAt: reminderAvailableAt.toISOString(),
  };
}

export function getQuarterlyProgressReportReviewSectionCurrentTotalInput(
  currentTotal: Pick<MonetaryAmount, 'currencyCode' | 'value'> | null | undefined
): MonetaryAmountInput | null {
  if (!currentTotal || typeof currentTotal.value !== 'number') {
    return null;
  }

  if (currentTotal.value < 0) {
    return null;
  }

  const roundedValue = Math.round(currentTotal.value);
  if (!Number.isSafeInteger(roundedValue)) {
    return null;
  }

  return {
    currencyCode: currentTotal.currencyCode,
    value: roundedValue,
  };
}

export function isQuarterlyProgressReportRelativePath(value: string) {
  return value.startsWith('/') && !value.startsWith('//') && !value.includes('\\');
}

export function appendQuarterlyProgressReportReturnTo(to: string, returnTo: string) {
  if (!isQuarterlyProgressReportRelativePath(to) || !isQuarterlyProgressReportRelativePath(returnTo)) {
    throw new Error('QPR return links require relative destination and return paths.');
  }

  const url = new URL(to, 'https://placeholder.invalid');
  url.searchParams.set('returnTo', returnTo);

  return `${url.pathname}${url.search}`;
}

export function getSafeQuarterlyProgressReportReturnTo({
  clientID,
  returnTo,
}: {
  clientID: string;
  returnTo: string | null;
}) {
  if (!returnTo) return undefined;
  if (!isQuarterlyProgressReportRelativePath(returnTo)) return undefined;

  const reviewPath = `/dashboard/${clientID}/progress-reports/verify-data`;
  const returnUrl = new URL(returnTo, 'https://placeholder.invalid');
  if (returnUrl.pathname !== reviewPath || returnUrl.searchParams.has('returnTo')) {
    return undefined;
  }

  return `${returnUrl.pathname}${returnUrl.search}`;
}

export function formatQuarterlyProgressReportTimestamp(
  date: string | null | undefined,
  locale: string,
  timeZone: string
) {
  if (!date) return null;
  const parsedDate = parseISO(date);
  if (!isValid(parsedDate)) return null;

  return new Intl.DateTimeFormat(locale, {
    ...quarterlyProgressReportDateFormatOptions,
    timeZone,
  }).format(parsedDate);
}

export function formatQuarterlyProgressReportDateTime(
  date: string | null | undefined,
  locale: string,
  timeZone: string
) {
  if (!date) return null;
  const parsedDate = parseISO(date);
  if (!isValid(parsedDate)) return null;

  return new Intl.DateTimeFormat(locale, {
    ...quarterlyProgressReportDateFormatOptions,
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(parsedDate);
}

export function getQuarterlyProgressReportTimeZone(isHydrated: boolean) {
  return isHydrated ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';
}

export function formatQuarterlyProgressReportDateOnly(date: string | null | undefined, locale: string) {
  if (!date) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const [year, month, day] = date.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  if (
    Number.isNaN(parsedDate.getTime()) ||
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    ...quarterlyProgressReportDateFormatOptions,
    timeZone: 'UTC',
  }).format(parsedDate);
}

export function formatQuarterlyProgressReportPeriod(review: QuarterlyProgressReportReviewPeriod, locale: string) {
  const periodStart = formatQuarterlyProgressReportDateOnly(review.periodStart, locale);
  const periodEnd = formatQuarterlyProgressReportDateOnly(review.periodEnd, locale);

  if (!periodStart || !periodEnd) return null;

  return `${periodStart} - ${periodEnd}`;
}
